import {
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  type Analytics,
} from 'firebase/analytics'
import { cookieYesConsentService } from '@core/services/cookie-consent/cookieyes-consent-service'
import { firebaseApp } from '@core/services/firebase/firebase-service'

type AnalyticsParamValue = string | number | boolean
export type AnalyticsEventParams = Record<
  string,
  AnalyticsParamValue | null | undefined
>

const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]{0,39}$/
const MAX_PARAMS = 25
const MAX_STRING_VALUE_LENGTH = 100

function sanitizeName(name: string): string | null {
  const normalizedName = name.trim()
  if (!NAME_REGEX.test(normalizedName)) {
    return null
  }
  return normalizedName
}

function sanitizeParamValue(
  value: AnalyticsParamValue | null | undefined,
): AnalyticsParamValue | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  if (typeof value === 'string') {
    return value.trim().slice(0, MAX_STRING_VALUE_LENGTH)
  }

  return value
}

function sanitizeParams(params: AnalyticsEventParams): Record<string, AnalyticsParamValue> {
  const entries = Object.entries(params)
  const sanitizedEntries: Array<[string, AnalyticsParamValue]> = []

  for (const [key, value] of entries) {
    if (sanitizedEntries.length >= MAX_PARAMS) {
      break
    }

    const sanitizedKey = sanitizeName(key)
    if (!sanitizedKey) {
      continue
    }

    const sanitizedValue = sanitizeParamValue(value)
    if (sanitizedValue === undefined) {
      continue
    }

    sanitizedEntries.push([sanitizedKey, sanitizedValue])
  }

  return Object.fromEntries(sanitizedEntries)
}

export class AnalyticsService {
  private static instance: AnalyticsService
  private analyticsInstance: Analytics | null = null
  private supportCheckPromise: Promise<boolean> | null = null
  private initializationAttempted = false

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  private async isBrowserAnalyticsSupported(): Promise<boolean> {
    if (!this.supportCheckPromise) {
      this.supportCheckPromise = isSupported().catch(() => false)
    }
    return this.supportCheckPromise
  }

  private async getAnalyticsInstance(): Promise<Analytics | null> {
    if (this.analyticsInstance) {
      return this.analyticsInstance
    }

    if (this.initializationAttempted) {
      return null
    }

    this.initializationAttempted = true

    if (typeof window === 'undefined' || !firebaseApp) {
      return null
    }

    const isSupportedInBrowser = await this.isBrowserAnalyticsSupported()
    if (!isSupportedInBrowser) {
      return null
    }

    try {
      this.analyticsInstance = getAnalytics(firebaseApp)
      return this.analyticsInstance
    } catch {
      return null
    }
  }

  public async logEventSafe(
    eventName: string,
    params: AnalyticsEventParams = {},
  ): Promise<void> {
    if (!cookieYesConsentService.isAnalyticsConsentGranted()) {
      return
    }

    const sanitizedEventName = sanitizeName(eventName)
    if (!sanitizedEventName) {
      return
    }

    const analyticsInstance = await this.getAnalyticsInstance()
    if (!analyticsInstance) {
      return
    }

    const sanitizedParams = sanitizeParams(params)

    try {
      firebaseLogEvent(analyticsInstance, sanitizedEventName, sanitizedParams)
    } catch {
      // Keep analytics fully non-blocking for user flows.
    }
  }
}
